import type { StoryElement } from '../types';
import { StoryRow } from './StoryRow';
import { useStoryKeyboardNav } from '../hooks/useStoryKeyboardNav';
import type { UserEventContext } from '../repositories/storyRepository';
import { FOR_YOU_CHANNEL_ID, savedSearchIdFromChannel } from '../utils/channelUrl';

interface StoryListProps {
  selectedChannelId: string;
  elements: StoryElement[];
  loading: boolean;
  populating?: boolean;
  isAdmin?: boolean;
  /** Admin overlay data is on its way; the rows below are the reader's. */
  adminLoading?: boolean;
  eventContext?: UserEventContext;
  /**
   * Deepen the feed. Presence is the whole signal: the owner of the list knows
   * whether there is more to fetch, so passing nothing hides the control
   * rather than asking this component to work it out from a row count.
   */
  onLoadMore?: () => void;
  loadingMore?: boolean;
}


export function StoryList({
  selectedChannelId,
  elements,
  loading,
  populating,
  isAdmin,
  adminLoading,
  onLoadMore,
  loadingMore,
  eventContext = selectedChannelId === 'tldr' ? { surface: 'tldr' } : { surface: 'channel', surfaceId: selectedChannelId },
}: StoryListProps) {
  const {
    visibleElements,
    focusedIndex,
    expandedIds,
    focusStory,
    toggleExpanded,
    setRowRef,
    notifyStoryUpdate,
  } = useStoryKeyboardNav(elements, selectedChannelId, eventContext);

  return (
    <section className="feed-list-pane">
      {adminLoading && (
        <div className="feed-admin-loading" role="status">
          <span className="spinner" /> Loading admin data…
        </div>
      )}
      <div className="feed-list">
        {populating && visibleElements.length === 0 ? (
          <div className="empty-panel">Loading...</div>
        ) : loading ? (
          <div className="empty-panel">Loading stories...</div>
        ) : visibleElements.length === 0 ? (
          <div className="empty-panel">You're all caught up.</div>
        ) : (
          visibleElements.map((story, i) => (
            <StoryRow
              key={story.id}
              story={story}
              isAdmin={isAdmin}
              compactReader={!isAdmin && (selectedChannelId === 'tldr' || selectedChannelId === FOR_YOU_CHANNEL_ID || savedSearchIdFromChannel(selectedChannelId) !== null)}
              isFocused={i === focusedIndex}
              rowRef={setRowRef(i)}
              isExpanded={expandedIds.has(story.id)}
              onFocusStory={focusStory}
              onToggleExpanded={toggleExpanded}
              onStoryUpdate={notifyStoryUpdate}
              eventContext={eventContext}
            />
          ))
        )}
        {onLoadMore && visibleElements.length > 0 && (
          <div className="feed-list-more">
            <button
              type="button"
              className="secondary-button"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading more stories…' : 'More stories'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
